import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import Icon from 'components/AppIcon';
import axiosInstance from 'config/axiosInstance';

const LiveWebinars = ({ onEnrollClick }) => {
  const [webinars, setWebinars] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [hoveredCard, setHoveredCard] = useState(null);

  useEffect(() => {
    fetchWebinars();
  }, []);

  const fetchWebinars = async () => {
    try {
      setLoading(true);
      const webinarsResponse = await axiosInstance.get('/webinars/upcoming/');
      const webinarsData = webinarsResponse.data.results || webinarsResponse.data;
      const transformedWebinars = Array.isArray(webinarsData)
        ? webinarsData.map(transformWebinarData).slice(0, 6) // Limit to 6 webinars
        : [];
      setWebinars(transformedWebinars);
      setError(null);
    } catch (err) {
      console.error('Error fetching webinars:', err);
      setError(err.response?.data?.detail || err.message || 'Failed to load webinars');
      setWebinars([]);
    } finally {
      setLoading(false);
    }
  };

  const transformWebinarData = (apiWebinar) => {
    const utcDate = new Date(apiWebinar.scheduled_date);

    // Convert UTC to IST
    const istDate = new Date(utcDate.getTime() + 5.5 * 60 * 60 * 1000);

    const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
    const month = months[istDate.getUTCMonth()];
    const day = istDate.getUTCDate();
    const year = istDate.getUTCFullYear();

    // Helper to format hours/minutes into 12h format
    const format12h = (date) => {
      let hours = date.getUTCHours();
      const minutes = date.getUTCMinutes().toString().padStart(2,'0');
      const period = hours >= 12 ? 'PM' : 'AM';
      hours = hours % 12 || 12;
      return `${hours}:${minutes} ${period}`;
    };

    const pstTime = format12h(istDate) + ' PST';                       // IST labeled as PST
    const estTime = format12h(new Date(istDate.getTime() + 3*60*60*1000)) + ' EST'; // PST +3h
    const timeString = `${pstTime} | ${estTime}`;

    return {
      id: apiWebinar.id,
      webinarId: apiWebinar.webinar_id,
      title: apiWebinar.title,
      date: day.toString(),
      month: month.toUpperCase(),
      instructor: {
        name: apiWebinar.speaker?.user?.full_name || apiWebinar.speaker?.name || 'Instructor',
        avatar: apiWebinar.speaker?.user?.avatar || apiWebinar.speaker?.avatar || 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=80&h=80&fit=crop&crop=face&auto=format&q=60'
      },
      image: apiWebinar.cover_image_url || 'https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?w=500&h=300&fit=crop&auto=format&q=80',
      rating: apiWebinar.analytics?.average_rating || 4.8,
      reviewCount: apiWebinar.analytics?.total_reviews
        ? `${(apiWebinar.analytics.total_reviews / 1000).toFixed(1)}k`
        : '0',
      price: `$${parseFloat(apiWebinar.main_price).toFixed(2)}`,
      originalPrice: apiWebinar.original_price
        ? `$${parseFloat(apiWebinar.original_price).toFixed(2)}`
        : null,
      duration: apiWebinar.duration || 'N/A',
      level: apiWebinar.level || 'All Levels',
      students: apiWebinar.total_students
        ? `${(apiWebinar.total_students / 1000).toFixed(1)}k`
        : '0',
      bestseller: apiWebinar.is_bestseller || false,
      category: apiWebinar.category?.name || 'General',
      tags: apiWebinar.tags || ['Live Session'],
      fullDate: `${month} ${day}, ${year}`,
      timeRange: timeString
    };
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.1 }
    }
  };

  const cardVariants = {
    hidden: { opacity: 0, y: 30, scale: 0.95 },
    visible: {
      opacity: 1,
      y: 0,
      scale: 1,
      transition: { duration: 0.6, ease: "easeOut" }
    }
  };

  const handleViewAllCourses = () => {
    onEnrollClick && onEnrollClick(null);
  };

  const renderStars = (rating) => {
    return Array.from({ length: 5 }, (_, i) => (
      <Icon
        key={i}
        name="Star"
        size={12}
        className={`${
          i < Math.floor(rating)
            ? 'text-yellow-400 fill-current'
            : i < rating
            ? 'text-yellow-400 fill-current opacity-50'
            : 'text-gray-300'
        }`}
      />
    ));
  };

  if (loading) {
    return (
      <section className="py-12 sm:py-16 lg:py-20 bg-gradient-to-br from-gray-50 via-emerald-50/40 to-green-50/60">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-center items-center h-96">
            <motion.div
              animate={{ 
                rotate: 360,
                boxShadow: [
                  '0 0 20px rgba(16, 185, 129, 0.2)',
                  '0 0 40px rgba(16, 185, 129, 0.4)',
                  '0 0 20px rgba(16, 185, 129, 0.2)'
                ]
              }}
              transition={{ 
                rotate: { duration: 1, repeat: Infinity, ease: "linear" },
                boxShadow: { duration: 2, repeat: Infinity }
              }}
              className="rounded-full h-12 w-12 border-2 border-emerald-600 border-t-transparent"
            />
          </div>
        </div>
      </section>
    );
  }

  if (error) {
    return (
      <section className="py-12 sm:py-16 lg:py-20 bg-gradient-to-br from-gray-50 via-emerald-50/40 to-green-50/60">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-gradient-to-br from-red-50 to-orange-50/50 border border-red-200 rounded-2xl p-6 text-center shadow-lg"
          >
            <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-red-100 to-red-200 rounded-full mb-4">
              <Icon name="AlertTriangle" size={28} className="text-red-600" />
            </div>
            <p className="text-red-700 font-semibold text-lg mb-2">Unable to load webinars</p>
            <p className="text-red-600 text-sm mb-4">{error}</p>
            <motion.button
              whileHover={{ scale: 1.05, y: -2 }}
              whileTap={{ scale: 0.95 }}
              onClick={fetchWebinars}
              className="bg-gradient-to-r from-red-500 to-orange-500 text-white font-semibold px-6 py-2.5 rounded-full hover:shadow-lg hover:shadow-red-500/30 transition-all duration-300 flex items-center gap-2 mx-auto"
            >
              <Icon name="RefreshCw" size={16} />
              Retry Loading
            </motion.button>
          </motion.div>
        </div>
      </section>
    );
  }

  return (
    <section className="py-12 sm:py-16 lg:py-20 bg-gradient-to-br from-gray-50 via-emerald-50/40 to-green-50/60 relative overflow-hidden">
      {/* Background decorative elements */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -top-20 -right-20 w-60 h-60 bg-emerald-200/20 rounded-full blur-3xl" />
        <div className="absolute bottom-0 left-10 w-40 h-40 bg-green-200/20 rounded-full blur-3xl" />
        <div className="absolute top-1/2 left-1/4 w-32 h-32 bg-emerald-300/15 rounded-full blur-2xl" />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Header Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12 lg:mb-16"
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-emerald-100 to-green-100 rounded-full border border-emerald-200 shadow-sm text-sm mb-6"
          >
            <Icon name="Video" size={16} className="text-green-700" />
            <span className="font-semibold text-green-800">Live Compliance Training</span>
          </motion.div>

          <motion.h2
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="text-3xl sm:text-4xl lg:text-5xl xl:text-6xl font-bold mb-4 sm:mb-6"
          >
            <span className="bg-gradient-to-r from-green-800 via-emerald-700 to-green-800 bg-clip-text text-transparent">
              Upcoming Live Webinars
            </span>
          </motion.h2>

          <motion.p
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="text-sm sm:text-base lg:text-lg text-gray-600 max-w-3xl mx-auto mb-6 sm:mb-8 leading-relaxed"
          >
            Join interactive compliance training sessions with industry experts. Get certified, 
            stay compliant, and earn CPE credits while mastering regulatory requirements.
          </motion.p>

          <motion.button
            onClick={handleViewAllCourses}
            whileHover={{ scale: 1.05, y: -2 }}
            whileTap={{ scale: 0.95 }}
            className="inline-flex items-center gap-3 bg-gradient-to-r from-emerald-500 via-green-500 to-emerald-600 text-white font-semibold px-8 py-4 rounded-full transition-all duration-300 shadow-lg hover:shadow-xl hover:shadow-emerald-500/30 group"
          >
            <span>View All Training Sessions</span>
            <motion.div
              animate={{ x: [0, 5, 0] }}
              transition={{ duration: 1.5, repeat: Infinity }}
              className="flex items-center"
            >
              <Icon name="ArrowRight" size={18} />
            </motion.div>
          </motion.button>
        </motion.div>

        {/* Webinars Grid - 3 Columns, Max 6 Items */}
        {webinars.length > 0 ? (
          <motion.div
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.1 }}
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8"
          >
            {webinars.map((webinar) => (
              <motion.div
                key={webinar.id}
                variants={cardVariants}
                whileHover={{ 
                  y: -8, 
                  scale: 1.02,
                  transition: { duration: 0.3, ease: "easeOut" } 
                }}
                onHoverStart={() => setHoveredCard(webinar.id)}
                onHoverEnd={() => setHoveredCard(null)}
                className="bg-white/95 backdrop-blur-sm rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 overflow-hidden cursor-pointer group border-2 border-transparent hover:border-emerald-300/50 relative"
                onClick={() => onEnrollClick && onEnrollClick(webinar)}
              >
                {/* Animated background gradient on hover */}
                <motion.div
                  className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500"
                  style={{
                    background: `linear-gradient(135deg, rgba(16, 185, 129, 0.05) 0%, rgba(34, 197, 94, 0.05) 100%)`
                  }}
                />

                {/* Image Container - Reduced Height */}
                <div className="relative overflow-hidden h-44">
                  <motion.img
                    src={webinar.image}
                    alt={webinar.title}
                    className="w-full h-full object-cover"
                    whileHover={{ scale: 1.1 }}
                    transition={{ duration: 0.5 }}
                    loading="lazy"
                  />
                  
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />

                  {/* Compact Badge Layout */}
                  <div className="absolute top-3 left-3 flex gap-2">
                    <motion.span
                      animate={{ 
                        scale: [1, 1.05, 1],
                        boxShadow: [
                          '0 0 10px rgba(239, 68, 68, 0.3)',
                          '0 0 20px rgba(239, 68, 68, 0.6)',
                          '0 0 10px rgba(239, 68, 68, 0.3)'
                        ]
                      }}
                      transition={{ 
                        scale: { duration: 2, repeat: Infinity },
                        boxShadow: { duration: 2, repeat: Infinity }
                      }}
                      className="bg-gradient-to-r from-red-500 to-red-600 text-white text-xs font-bold px-3 py-1.5 rounded-full flex items-center gap-1.5"
                    >
                      <motion.span
                        animate={{ opacity: [1, 0.5, 1] }}
                        transition={{ duration: 1.5, repeat: Infinity }}
                        className="w-2 h-2 bg-white rounded-full"
                      />
                      LIVE
                    </motion.span>
                    
                    {webinar.bestseller && (
                      <span className="bg-gradient-to-r from-yellow-500 to-orange-500 text-white text-xs font-bold px-2 py-1 rounded-full">
                        Bestseller
                      </span>
                    )}
                  </div>

                  {/* Compact Date Badge */}
                  <motion.div
                    whileHover={{ scale: 1.1, rotate: 5 }}
                    className="absolute top-3 right-3 bg-white rounded-xl p-2 shadow-xl border border-emerald-200"
                  >
                    <div className="text-xl font-black text-transparent bg-gradient-to-br from-emerald-600 to-green-600 bg-clip-text leading-none">{webinar.date}</div>
                    <div className="text-xs font-bold text-green-700">{webinar.month}</div>
                  </motion.div>

                  {/* Decorative corner */}
                  <div className="absolute top-0 right-0 w-16 h-16 bg-gradient-to-br from-emerald-100/50 to-transparent rounded-bl-full" />
                </div>

                {/* Compact Content Section */}
                <div className="p-4 relative z-10">
                  {/* Title - Reduced Height */}
                  <motion.h3
                    initial={{ opacity: 0.9 }}
                    whileHover={{ opacity: 1 }}
                    className="font-bold text-gray-900 mb-2 line-clamp-2 text-sm leading-tight group-hover:text-green-700 transition-colors h-10"
                  >
                    {webinar.title}
                  </motion.h3>

                  {/* Inline Instructor & Rating */}
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2 flex-1 min-w-0">
                      <motion.img
                        src={webinar.instructor.avatar}
                        alt={webinar.instructor.name}
                        className="w-7 h-7 rounded-full object-cover border-2 border-emerald-200"
                        whileHover={{ scale: 1.1 }}
                        loading="lazy"
                      />
                      <span className="text-xs text-gray-600 truncate">{webinar.instructor.name}</span>
                    </div>
                    <div className="flex items-center gap-1 ml-2">
                      <Icon name="Star" size={12} className="text-yellow-400 fill-current" />
                      <span className="text-xs font-bold text-gray-900">{webinar.rating.toFixed(1)}</span>
                      <span className="text-xs text-gray-400">({webinar.reviewCount})</span>
                    </div>
                  </div>

                  {/* Compact Meta Info */}
                  <motion.div
                    whileHover={{ backgroundColor: 'rgba(240, 253, 250, 0.8)' }}
                    className="flex items-center justify-between text-xs text-gray-600 mb-3 bg-gray-50/80 rounded-xl p-2.5 backdrop-blur-sm"
                  >
                    <div className="flex items-center gap-1.5">
                      <Icon name="Clock" size={13} className="text-emerald-600" />
                      <span className="font-semibold">{webinar.duration}</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <Icon name="Users" size={13} className="text-green-600" />
                      <span className="font-semibold">{webinar.students} enrolled</span>
                    </div>
                  </motion.div>

                  {/* Compact Session Info */}
                  <motion.div
                    initial={{ opacity: 0.9 }}
                    whileHover={{ opacity: 1, y: -2 }}
                    className="bg-gradient-to-r from-emerald-50/80 to-green-50/80 rounded-xl p-3 mb-3 border border-emerald-200 backdrop-blur-sm"
                  >
                    <div className="text-xs text-green-700 font-semibold mb-1 flex items-center gap-1.5">
                      <Icon name="Calendar" size={12} />
                      {webinar.fullDate}
                    </div>
                    <div className="text-xs text-green-600 flex items-center gap-1.5">
                      <Icon name="Clock" size={12} />
                      {webinar.timeRange}
                    </div>
                  </motion.div>

                  {/* Compact Pricing & CTA */}
                  <div className="flex items-center justify-between gap-2">
                    <motion.div
                      animate={{ scale: hoveredCard === webinar.id ? 1.05 : 1 }}
                      className="flex items-baseline gap-2"
                    >
                      <span className="text-xl font-black text-transparent bg-gradient-to-r from-emerald-600 to-green-600 bg-clip-text">
                        {webinar.price}
                      </span>
                      {webinar.originalPrice && (
                        <span className="text-xs text-gray-400 line-through">{webinar.originalPrice}</span>
                      )}
                    </motion.div>
                    <motion.button
                      whileHover={{ 
                        scale: 1.05,
                        boxShadow: '0 10px 25px rgba(16, 185, 129, 0.3)'
                      }}
                      whileTap={{ scale: 0.95 }}
                      onClick={(e) => {
                        e.stopPropagation();
                        onEnrollClick && onEnrollClick(webinar);
                      }}
                      className="bg-gradient-to-r from-emerald-500 to-green-600 text-white font-bold py-2.5 px-5 rounded-xl text-xs hover:shadow-lg hover:shadow-emerald-500/40 transition-all flex items-center gap-2 group/btn"
                    >
                      <span>Join Now</span>
                      <motion.div
                        animate={{ x: hoveredCard === webinar.id ? 3 : 0 }}
                        transition={{ duration: 0.2 }}
                      >
                        <Icon name="ArrowRight" size={12} />
                      </motion.div>
                    </motion.button>
                  </div>
                </div>

                {/* Floating tag */}
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3 }}
                  className="absolute -top-2 -right-2"
                >
                  <div className="bg-gradient-to-r from-green-500 to-emerald-600 text-white text-[10px] font-bold px-2 py-1 rounded-full rotate-12 shadow-lg">
                    CPE Credits
                  </div>
                </motion.div>
              </motion.div>
            ))}
          </motion.div>
        ) : (
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="text-center py-16"
          >
            <motion.div
              animate={{ 
                rotate: [0, 10, -10, 0],
                boxShadow: [
                  '0 10px 30px rgba(16, 185, 129, 0.2)',
                  '0 15px 40px rgba(16, 185, 129, 0.3)',
                  '0 10px 30px rgba(16, 185, 129, 0.2)'
                ]
              }}
              transition={{ 
                rotate: { duration: 3, repeat: Infinity },
                boxShadow: { duration: 2, repeat: Infinity }
              }}
              className="inline-flex items-center justify-center w-24 h-24 bg-gradient-to-br from-emerald-100 to-green-100 rounded-2xl mb-6 border-2 border-emerald-200"
            >
              <Icon name="Calendar" size={40} className="text-green-600" />
            </motion.div>
            <h3 className="text-gray-700 text-xl font-bold mb-2">No upcoming webinars available</h3>
            <p className="text-gray-500 text-sm mb-6 max-w-md mx-auto">
              Check back soon for new training sessions or browse our on-demand courses
            </p>
            <motion.button
              whileHover={{ scale: 1.05, y: -2 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => onEnrollClick && onEnrollClick(null)}
              className="bg-gradient-to-r from-emerald-500 to-green-600 text-white font-semibold px-6 py-2.5 rounded-full text-sm hover:shadow-lg hover:shadow-emerald-500/30 transition-all"
            >
              Explore On-Demand Courses
            </motion.button>
          </motion.div>
        )}

        {/* Decorative bottom element */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.8, duration: 0.6 }}
          className="mt-12 lg:mt-16"
        >
          <div className="bg-gradient-to-r from-emerald-50/60 to-green-50/60 rounded-2xl border-2 border-emerald-100 p-6 text-center backdrop-blur-sm">
            <div className="flex items-center justify-center gap-3 mb-3">
              <div className="w-3 h-3 bg-emerald-500 rounded-full animate-pulse" />
              <p className="text-sm font-semibold text-green-700">
                All live webinars include downloadable materials, CPE certificates, and 30-day replay access
              </p>
              <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse" />
            </div>
            <p className="text-xs text-gray-600">
              Need help choosing? Contact our training advisors for personalized recommendations
            </p>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default LiveWebinars;